import React, { useState } from "react";
import {
  LookupDto,
  ServiceRequestDashboardCountDto,
  ServiceRequestGridDto,
  ServiceRequestLookupsDto,
  ServiceRequestSearchDto,
  SortModel,
} from "../../services/dto";
import useServiceRequestStyles from "./styles";
import { ServiceRequestService } from "./serviceRequestService";
import { Constants } from "../../assets/utils/lib/constants";
import { SortDirection } from "../../assets/utils/lib";
import { SearchContainer } from "../../assets/vfx/search/searchContainer";
import { Col, Row } from "reactstrap";
import Form from "../../assets/vfx/form/form";
import { StatCard, GridCard } from "../../assets/vfx";
import { PageContent } from "../../assets/layouts";
import { FormLabel } from "../../assets/vfx/form/components";
import Control from "../../assets/vfx/controls/control";

interface ServiceRequestPageProps {
  data?: ServiceRequestGridDto[];
  dashboardCounts: ServiceRequestDashboardCountDto | undefined;
  show: boolean;
  isLoading?: boolean;
  lookups?: ServiceRequestLookupsDto;
  isLoadingLookups?: boolean;
  onRowClick: (id: string) => void;
  onNew?: () => void;
  enabled?: boolean;
}

const defaultSortModel: SortModel = {
  field: "requestNumber",
  direction: SortDirection.Descending,
};

const createDefaultSearchQuery = (statusId = Constants.emptyGuid): ServiceRequestSearchDto => ({
  pageNumber: 1,
  pageSize: 9,
  departmentId: Constants.emptyGuid,
  requestTypeId: Constants.emptyGuid,
  statusId,
  sortBy: defaultSortModel,
  requestNumber: Constants.emptyString,
});

export const ServiceRequestPage: React.FC<ServiceRequestPageProps> = (
  props,
) => {
  const styles = useServiceRequestStyles();
  const getStatusId = (display: string) =>
    props.lookups?.status?.find(
      (status) => status.display?.toLowerCase() === display.toLowerCase(),
    )?.id;

  const defaultSearchQuery = createDefaultSearchQuery(
    getStatusId("Open"),
  );
  const [searchQuery, setSearchQuery] =
    useState<ServiceRequestSearchDto>(() => defaultSearchQuery);

  const priorityBadge = (priority: string) => {
    let priorityStyle = styles.priorityLow;
    if (priority === "High") priorityStyle = styles.priorityHigh;
    else if (priority === "Medium") priorityStyle = styles.priorityMedium;

    return (
      <Control.Chip
        pill
        aria-label={`${priority} priority`}
        title={`${priority} Priority`}
        style={{ ...styles.priorityChip, ...priorityStyle }}
      >
        {priority}
      </Control.Chip>
    );
  };

  const statusDot = (status: string) => {
    if (status === "Open")
      return <div style={{ ...styles.statusSquare, ...styles.dotOpen }} />;
    if (status === "In Progress")
      return <div style={{ ...styles.statusSquare, ...styles.dotProgress }} />;
    return <div style={{ ...styles.statusSquare, ...styles.dotClosed }} />;
  };

  const serviceRequestGridQuery = ServiceRequestService.gridQuery({
    ...searchQuery,
    departmentId:
      searchQuery.departmentId === Constants.emptyGuid
        ? undefined
        : searchQuery.departmentId,
    requestTypeId:
      searchQuery.requestTypeId === Constants.emptyGuid
        ? undefined
        : searchQuery.requestTypeId,
    statusId:
      searchQuery.statusId === Constants.emptyGuid
        ? undefined
        : searchQuery.statusId,
  }, props.enabled);

  const rows = serviceRequestGridQuery.data?.result ?? [];
  const totalRecords = serviceRequestGridQuery.data?.totalSize ?? 0;
  const totalPages = Math.ceil(totalRecords / (searchQuery.pageSize ?? 9));

  const handleSearch = (values: ServiceRequestSearchDto) => {
    setSearchQuery((prev) => ({
      ...prev,
      requestNumber: values.requestNumber ?? Constants.emptyString,
      departmentId: values.departmentId ?? Constants.emptyGuid,
      requestTypeId: values.requestTypeId ?? Constants.emptyGuid,
      statusId: values.statusId ?? Constants.emptyGuid,
      pageNumber: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setSearchQuery((prev) => ({
      ...prev,
      pageNumber: page,
    }));
  };

  const handlePageSizeChange = (size: number) => {
    setSearchQuery((prev) => ({
      ...prev,
      pageSize: size,
      pageNumber: 1,
    }));
  };

  const openCount = props.dashboardCounts ? props.dashboardCounts.openCount : 0;
  const progressCount = props.dashboardCounts
    ? props.dashboardCounts.inProgressCount
    : 0;
  const closedCount = props.dashboardCounts
    ? props.dashboardCounts.closedCount
    : 0;

  const [formResetKey, setFormResetKey] = useState(0);

  const handleClear = () => {
    setSearchQuery(defaultSearchQuery);
    setFormResetKey((k) => k + 1);
  };

  const handleStatusCardClick = (status: string) => {
    setSearchQuery((prev) => ({
      ...prev,
      statusId: getStatusId(status) ?? Constants.emptyGuid,
      pageNumber: 1,
    }));
    setFormResetKey((k) => k + 1);
  };

  return (
    <PageContent title="Service Requests">
      <Row>
        <Col>
          <StatCard
            variant="orange"
            value={openCount}
            label="Open Requests"
            onClick={() => handleStatusCardClick("Open")}
          />
        </Col>
        <Col>
          <StatCard
            variant="green"
            value={progressCount}
            label="In Progress"
            onClick={() => handleStatusCardClick("In Progress")}
          />
        </Col>
        <Col>
          <StatCard
            variant="blue"
            value={closedCount}
            label="Closed"
            onClick={() => handleStatusCardClick("Closed")}
          />
        </Col>
      </Row>

      <FormLabel
        style={{
          marginTop: 10,
          marginBottom: 10,
          fontSize: 20,
          fontWeight: 700,
        }}
      >
      </FormLabel>

      <div>
        <SearchContainer
          key={formResetKey}
          controlStyles={styles.search}
          initialValues={{
            requestNumber: searchQuery.requestNumber,
            departmentId: searchQuery.departmentId,
            requestTypeId: searchQuery.requestTypeId,
            statusId: searchQuery.statusId,
          }}
          onSearch={handleSearch}
          onClear={handleClear}
          onNew={props.onNew}
          enableReinitialize
        >
          <Col lg={3} style={styles.searchColumn}>
            <Form.Text
              name="requestNumber"
              label="Request Number"
              groupType="topLabel"
              controlStyles={styles.searchControlStyles}
              style={styles.searchControl}
            />
          </Col>
          <Col lg={2} style={styles.searchColumn}>
            <Form.Dropdown
              name="departmentId"
              label="Department"
              groupType="topLabel"
              controlStyles={styles.searchControlStyles}
              style={styles.searchControl}
              options={LookupDto.mapToOptions(props.lookups?.department)}
              isLoading={props.isLoading}
              blankOption={{
                key: Constants.emptyGuid,
                display: "All",
                value: Constants.emptyGuid,
              }}
            />
          </Col>

          <Col lg={2} style={styles.searchColumn}>
            <Form.Dropdown
              name="requestTypeId"
              label="Request Type"
              groupType="topLabel"
              controlStyles={styles.searchControlStyles}
              style={styles.searchControl}
              options={LookupDto.mapToOptions(props.lookups?.requestType)}
              isLoading={props.isLoading}
              blankOption={{
                key: Constants.emptyGuid,
                display: "All",
                value: Constants.emptyGuid,
              }}
            />
          </Col>

          <Col lg={2} style={styles.searchColumn}>
            <Form.Dropdown
              name="statusId"
              label="Status"
              groupType="topLabel"
              controlStyles={styles.searchControlStyles}
              style={styles.searchControl}
              options={LookupDto.mapToOptions(props.lookups?.status)}
              isLoading={props.isLoading}
              blankOption={{
                key: Constants.emptyGuid,
                display: "All",
                value: Constants.emptyGuid,
              }}
            />
          </Col>
        </SearchContainer>
      </div>

      <GridCard
        data={rows}
        emptyMessage="No service requests found."
        renderCard={(item) => ({
          title: item.requestNumber,
          subtitle: item.requestType,
          badge: priorityBadge(item.priority ?? ""),
          children: <>{item.addressLine1} {item.city}</>,
          footer: (
            <>
              <div style={styles.srStatus}>
                {statusDot(item.status ?? "")}
                {item.status}
              </div>
              <button
                style={styles.btnView}
                onClick={(e) => {
                  e.stopPropagation();
                  props.onRowClick(item.id);
                }}
              >
                View
              </button>
            </>
          ),
        })}
        currentPage={searchQuery.pageNumber ?? 1}
        totalPages={totalPages}
        pageSize={searchQuery.pageSize ?? 9}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </PageContent>
  );
};
